import { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

// Footymatch install-prompt.
//
// Two paths — Chrome/Android fires `beforeinstallprompt` which we capture and
// re-fire on user click. Safari/iOS never fires the event, so we render a
// short "Add to Home Screen" hint the first time an iOS user lands on the app.
//
// Both are dismissible; both remember their choice via localStorage so we
// don't nag anyone. Bar sits above the bottom nav so it's clearly opt-in.
const LS_KEY = "fm_install_dismissed_at";
const NAG_COOLDOWN_DAYS = 14; // don't re-prompt for 2 weeks after dismissal

const isIos = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  // iOS uses navigator.standalone; every other browser uses the display-mode MQ.
  if (typeof window === "undefined") return false;
  return (
    (typeof navigator !== "undefined" && navigator.standalone === true) ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
  );
};

const wasRecentlyDismissed = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return false;
    const then = new Date(raw).getTime();
    if (Number.isNaN(then)) return false;
    return (Date.now() - then) < NAG_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const markDismissed = () => {
  try { localStorage.setItem(LS_KEY, new Date().toISOString()); } catch { /* ignore */ }
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    // Android/Chrome path — the browser tells us the app is installable.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Clean up state once the user actually installs.
    const onInstalled = () => {
      setDeferred(null);
      setShowIosHint(false);
      markDismissed();
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS path — show the hint on a small delay so it doesn't smack them on load.
    if (isIos()) {
      const t = setTimeout(() => setShowIosHint(true), 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignored */ }
    setDeferred(null);
    markDismissed();
  };

  const dismiss = () => {
    setDismissed(true);
    markDismissed();
  };

  if (dismissed) return null;

  // Chrome / Android — one-tap install
  if (deferred) {
    return (
      <div
        className="fixed left-3 right-3 bottom-24 sm:left-auto sm:right-6 sm:w-96 z-40 rounded-2xl border border-white/10 bg-footy-card/95 backdrop-blur-md shadow-2xl p-4 flex items-center gap-3"
        data-testid="install-prompt-android"
      >
        <div className="w-11 h-11 rounded-xl bg-footy-red/15 border border-footy-red/40 text-footy-red flex items-center justify-center flex-none">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-tight">Install Footymatch</div>
          <p className="text-[11px] text-slate-400 mt-0.5">Add to your home screen for one-tap access — no App Store needed.</p>
        </div>
        <button onClick={install} className="footy-btn-primary !py-2 !px-3 !text-[11px] flex-none" data-testid="install-prompt-install">
          Install
        </button>
        <button onClick={dismiss} className="w-8 h-8 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 flex items-center justify-center flex-none" aria-label="Dismiss" data-testid="install-prompt-dismiss">
          <X size={14} />
        </button>
      </div>
    );
  }

  // iOS — Safari can't auto-install, so we show them how.
  if (showIosHint) {
    return (
      <div
        className="fixed left-3 right-3 bottom-24 z-40 rounded-2xl border border-white/10 bg-footy-card/95 backdrop-blur-md shadow-2xl p-4"
        data-testid="install-prompt-ios"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-footy-red/15 border border-footy-red/40 text-footy-red flex items-center justify-center flex-none">
            <Download size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">Get Footymatch on your home screen</div>
            <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
              Tap <Share size={11} className="inline mx-0.5 align-[-2px]" /> in Safari, then <strong className="text-white">Add to Home Screen</strong> <Plus size={11} className="inline mx-0.5 align-[-2px]" /> — Footymatch will feel like a proper app.
            </p>
          </div>
          <button onClick={dismiss} className="w-8 h-8 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 flex items-center justify-center flex-none" aria-label="Dismiss" data-testid="install-prompt-dismiss-ios">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
